import React from "react";

// "brand" is Coharts' own blue — the site-wide default. "foresight" is reserved
// for Foresight-specific content, where violet is that product's own accent.
const TONE = {
  brand: {
    badge: "bg-[#1E4FD8]/10 text-[#1E4FD8] dark:bg-[#4A90D9]/15 dark:text-[#8CC0F0]",
    bar: "bg-[#1E4FD8] dark:bg-[#4A90D9]",
    text: "text-[#1E4FD8] dark:text-[#8CC0F0]",
  },
  foresight: {
    badge: "bg-[#5B21B6]/10 text-[#5B21B6] dark:bg-[#7C4DFF]/15 dark:text-[#B9A5FF]",
    bar: "bg-[#5B21B6] dark:bg-[#7C4DFF]",
    text: "text-[#5B21B6] dark:text-[#B9A5FF]",
  },
} as const;

export function Tag({
  children,
  tone = "brand",
  className = "",
}: {
  children: React.ReactNode;
  tone?: keyof typeof TONE;
  className?: string;
}) {
  return (
    <span className={`inline-block text-[11px] font-semibold uppercase tracking-widest px-3 py-1 rounded-full ${TONE[tone].badge} ${className}`}>
      {children}
    </span>
  );
}

export function SectionLabel({
  children,
  tone = "brand",
  className = "",
}: {
  children: React.ReactNode;
  tone?: keyof typeof TONE;
  className?: string;
}) {
  return (
    <div className="flex items-center gap-2 mb-3">
      <div className={`h-px w-6 ${TONE[tone].bar}`} />
      <span className={`text-xs font-semibold uppercase tracking-[0.2em] ${TONE[tone].text} ${className}`}>
        {children}
      </span>
    </div>
  );
}

export const H1 = React.forwardRef<HTMLHeadingElement, { children: React.ReactNode; className?: string }>(
  function H1({ children, className = "" }, ref) {
    return (
      <h1
        ref={ref}
        className={`font-display text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-medium leading-[1.02] tracking-[-0.02em] text-[#0A0A0F] dark:text-[#F5F5F7] ${className}`}
      >
        {children}
      </h1>
    );
  }
);

export function H2({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return (
    <h2 className={`font-display text-4xl md:text-5xl lg:text-6xl font-medium leading-[1.05] tracking-[-0.015em] text-[#0A0A0F] dark:text-[#F5F5F7] ${className}`}>
      {children}
    </h2>
  );
}

export function H3({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return (
    <h3 className={`text-xl font-medium tracking-[-0.01em] text-[#0A0A0F] dark:text-[#F5F5F7] ${className}`}>
      {children}
    </h3>
  );
}

export function Body({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return (
    <p className={`text-base font-light leading-[1.7] text-[#5C5C66] dark:text-[#9A9A9A] ${className}`}>
      {children}
    </p>
  );
}
